import { useState } from 'react';
import { Image as ImageIcon, Loader2, MessageSquare, X } from 'lucide-react';
import type { ChatDrawing } from '@/services/api';

/**
 * "You asked for a drawing. Shall I make it?" — asked before the turn does any work.
 *
 * This is not part of an answer and never becomes one. The turn stopped at its first
 * classification, which is the one that already runs on every message to decide which
 * documents are needed: no document has been fetched, no answer written, and nothing
 * drawn. That is the point of asking here rather than under a finished answer — an image
 * costs real money per attempt and most of a minute, and so does retrieval for an answer
 * nobody wanted.
 *
 * Three answers, because two would be wrong. Drawing is one. Answering in words is
 * another, and it is the one most often wanted: "draw a flowchart of the method in claim
 * 1" is usually worth reading as prose first. And dropping the message is the third,
 * because the quickest fix for a request that was read oddly is to rewrite it — so cancel
 * puts the text back in the box rather than merely dismissing the card.
 */

export default function ChatDrawingAsk({
  drawing,
  onDecide,
}: {
  drawing: ChatDrawing;
  onDecide: (decision: 'generate' | 'answer' | 'cancel') => Promise<string | void>;
}) {
  const [busy, setBusy] = useState<'generate' | 'answer' | 'cancel' | null>(null);
  const [failed, setFailed] = useState('');

  const decide = async (decision: 'generate' | 'answer' | 'cancel') => {
    setBusy(decision);
    setFailed('');
    const problem = await onDecide(decision);
    if (problem) {
      setFailed(problem);
      setBusy(null);
    }
    // No clearing on success: the card is replaced by what happens next.
  };

  return (
    <div className="max-w-2xl rounded-xl border border-blue-200 bg-blue-50/60 px-4 py-3">
      <div className="flex items-center gap-2 text-sm font-medium text-gray-900">
        <ImageIcon className="h-4 w-4 text-blue-600" />
        Make a drawing?
      </div>

      {/* Their own words, not a paraphrase. Nothing has been read yet, so a summary here
          would be a guess presented as understanding. */}
      <p className="mt-1.5 text-xs italic leading-relaxed text-gray-700">
        “{drawing.summary || drawing.request}”
      </p>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button
          onClick={() => decide('generate')}
          disabled={busy !== null}
          className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-60"
        >
          {busy === 'generate' ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <ImageIcon className="h-3.5 w-3.5" />
          )}
          Draw it
        </button>

        <button
          onClick={() => decide('answer')}
          disabled={busy !== null}
          className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
        >
          {busy === 'answer' ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <MessageSquare className="h-3.5 w-3.5" />
          )}
          Just answer instead
        </button>

        <button
          onClick={() => decide('cancel')}
          disabled={busy !== null}
          className="inline-flex items-center gap-1 rounded-md px-2 py-1.5 text-xs text-gray-500 hover:text-gray-800 disabled:opacity-60"
          title="Drop this message and put it back in the box"
        >
          <X className="h-3.5 w-3.5" />
          Cancel
        </button>
      </div>

      {failed && <p className="mt-2 text-xs text-red-700">{failed}</p>}

      <p className="mt-2 text-[10px] leading-snug text-gray-500">
        Drawing takes about a minute and reads the documents of this conversation first.
        A working drawing to think with or to put in a provisional — not a formal drawing
        under 37 CFR 1.84.
      </p>
    </div>
  );
}
